<script setup lang="ts">
import { computed } from 'vue'
import Spinner from '@/components/Base/Spinner.vue'
import { Button, Icon, type IconName, Modal } from '@easybell/caramel-vue3'

const props = withDefaults(
  defineProps<{
    modalType?: 'warning' | 'info' | 'success' | 'error'
    primaryButtonText?: string
    secondaryButtonText?: string
    text?: string
    title?: string
    isPrimaryButtonVisible?: boolean
    isSecondaryButtonVisible?: boolean
    isLoading?: boolean
    matomoContext?: string
  }>(),
  {
    modalType: 'info',
    title: '',
    text: '',
    isPrimaryButtonVisible: true,
    isSecondaryButtonVisible: true,
    isLoading: false,
    matomoContext: '',
  }
)

const emit = defineEmits<{
  (e: 'secondaryButtonClick', event: MouseEvent): void
  (e: 'primaryButtonClick', event: MouseEvent): void
}>()

const statusIcon = computed((): IconName => {
  switch (props.modalType) {
    case 'warning':
      return 'Warning'
    case 'success':
      return 'Checkmark'
    case 'info':
      return 'Info'
    case 'error':
      return 'Close'
  }
})
</script>

<template>
  <Modal>
    <template #body>
      <div class="flex flex-col gap-5">
        <div
          class="w-12 h-12 rounded-full mx-auto flex items-center justify-center"
          :class="{
            'bg-success-100': modalType === 'success',
            'bg-danger-100': modalType === 'error',
            'bg-primary-100': modalType === 'info',
            'bg-warning-100': modalType === 'warning',
          }"
        >
          <Icon
            size="1.5rem"
            :name="statusIcon"
            :class="{
              'text-success-700': modalType === 'success',
              'text-danger-700': modalType === 'error',
              'text-primary-700': modalType === 'info',
              'text-warning-700': modalType === 'warning',
            }"
          />
        </div>

        <div
          class="flex flex-col gap-2"
          :class="{
            'opacity-25': isLoading,
          }"
        >
          <div
            class="text-lg font-semibold text-center"
            data-test="modal-title"
            :class="{
              'text-danger-600': modalType === 'error',
            }"
          >
            <slot name="title">
              {{ title }}
            </slot>
          </div>

          <div
            class="text-sm text-center text-neutral-500"
            data-test="modal-text"
          >
            <slot name="text">
              <div v-html="text"></div>
            </slot>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="absolute block right-1/2 top-1/2">
        <Spinner class="mt-[-24px] mr-[-24px]" />
      </div>
    </template>

    <!-- Action buttons -->
    <template #footer v-if="isSecondaryButtonVisible || isPrimaryButtonVisible">
      <div>
        <div class="flex flex-col md:flex-row-reverse gap-4">
          <!-- Next button -->
          <Button
            v-if="isPrimaryButtonVisible"
            :variant="modalType === 'warning' ? 'danger' : 'primary'"
            size="base"
            type="button"
            class="w-full"
            data-test="modal-primary-button"
            :disabled="isLoading"
            v-track-matomo-click="[matomoContext, 'Primary button']"
            @click="emit('primaryButtonClick', $event)"
          >
            <slot name="primaryButton">
              {{ primaryButtonText ?? $t('global.action.ok') }}
            </slot>
          </Button>

          <!-- Cancel button -->
          <Button
            v-if="isSecondaryButtonVisible"
            :variant="modalType === 'warning' ? 'outlined' : 'outlined-primary'"
            size="base"
            type="button"
            data-test="modal-secondary-button"
            class="w-full"
            :disabled="isLoading"
            v-track-matomo-click="[matomoContext, 'Secondary button']"
            @click="emit('secondaryButtonClick', $event)"
          >
            <slot name="secondaryButton">
              {{ secondaryButtonText ?? $t('global.action.cancel') }}
            </slot>
          </Button>
        </div>
      </div>
    </template>
  </Modal>
</template>
